// File: src/screens/profile/tabs/ProfileTab.js
import React, { useEffect, useState } from "react";
import api from "../../../api/client";
import { fetchMyAddresses } from "../../../api/userAddresses";
import { uploadProfileAvatar } from "../../../api/userProfile";
import logger from "../../../utils/logger.js";
import {
  User,
  Camera,
  Briefcase,
  MapPin,
  Lock,
  Globe,
  Languages,
  Wrench,
  CheckCircle2,
  Save,
  Sparkles,
  ShieldCheck
} from "lucide-react";


function getStorageBaseUrl() {
  let apiUrl = process.env.REACT_APP_API_URL || "";

  if (!apiUrl && typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  if (apiUrl.startsWith("/")) {
    const origin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
    apiUrl = `${origin}${apiUrl}`;
  }

  return apiUrl
    .replace(/\/api(\/v1)?\/?$/, "")
    .replace(/\/laravel-api\/?$/, "")
    .replace(/\/$/, "");
}

function toAbsoluteUrl(raw) {
  const u = String(raw || "").trim();
  if (!u) return "";

  if (/^(blob:|data:)/i.test(u)) return u;

  const storageBase = getStorageBaseUrl();

  if (u.includes("/storage/")) {
    const storagePath = u.substring(u.indexOf("/storage/"));
    let clean = storagePath.replace(/^\/+/, "");
    if (clean.startsWith("storage/")) {
      clean = clean.replace(/^storage\//, "");
    }
    const fullPath = `${storageBase}/storage/${clean}`;
    if (fullPath.startsWith("/") && typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}${fullPath}`;
    }
    return fullPath;
  }

  if (/^https?:\/\//i.test(u)) return u;

  let clean = u.replace(/^\/+/, "");

  if (clean.startsWith("laravel-api/")) {
    clean = clean.replace(/^laravel-api\//, "");
  }

  if (clean.startsWith("storage/")) {
    clean = clean.replace(/^storage\//, "");
  }

  if (!clean.includes("/") && /\.(png|jpe?g|gif|webp|svg)$/i.test(clean)) {
    clean = `avatars/${clean}`;
  }

  const fullPath = `${storageBase}/storage/${clean}`;

  if (fullPath.startsWith("/")) {
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    return `${origin}${fullPath}`;
  }

  return fullPath;
}

function getValidationMessage(error, isAr) {
  const errors = error?.body?.errors;
  if (errors && typeof errors === "object") {
    const messages = Object.values(errors).flat().filter(Boolean);
    if (messages.length) return messages.join("\n");
  }

  if (error?.status >= 500) {
    return isAr
      ? "تعذر على الخادم حفظ ملف المستقل. تم إرسال البيانات المطلوبة، ويحتاج الخطأ إلى مراجعة سجل Laravel لدى الباك إند."
      : "The server could not save the freelancer profile. The required data was sent; please check the Laravel server log.";
  }

  const rawMsg = error?.message || "";
  if (rawMsg.includes("could not be found") || error?.status === 404) {
    return isAr
      ? "تنبيه: مسار ملف المستقل غير متاح حالياً على السيرفر البعيد. يمكنك الاستمرار بتحديث البيانات الشخصية."
      : "Notice: Freelancer profile route is not available on the remote server yet.";
  }

  return rawMsg || (isAr ? "تعذر حفظ بيانات المستقل." : "Unable to save freelancer profile.");
}

function listToDisplayValue(items) {
  if (!Array.isArray(items)) return "";
  return items
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(Boolean)
    .join(", ");
}

function listToItems(value) {
  return String(value || "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

export default function ProfileTab({
  user,
  setUser,
  form,
  setForm,
  isAr,
  labels,
  onOpenAddresses,
  onProfileUpdated,
}) {
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarDataUrl, setAvatarDataUrl] = useState(""); // persists after save for display
  const [avatarImgError, setAvatarImgError] = useState(false); // tracks img load failure
  const [avatarError, setAvatarError] = useState("");
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [savingPersonalInfo, setSavingPersonalInfo] = useState(false);
  const [savingFreelancerData, setSavingFreelancerData] = useState(false);
  const [savingSecurity, setSavingSecurity] = useState(false);

  // Freelancer Profile States
  const [freelancerData, setFreelancerData] = useState({
    headline: "",
    bio: "",
    website: "",
    languagesString: "",
    skillsString: "",
  });
  const [hasFreelancerProfile, setHasFreelancerProfile] = useState(false);
  const [loadingFreelancer, setLoadingFreelancer] = useState(true);
  const [freelancerError, setFreelancerError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function loadFreelancerProfile() {
      try {
        let res;
        try {
          res = await api.apiGet("v1/freelancer/profile/me");
        } catch (err1) {
          try {
            res = await api.apiGet("v1/freelancer/profile");
          } catch (err2) {
            res = await api.apiGet("freelancer/profile/me");
          }
        }
        if (cancelled) return;
        const fData = res?.data || res;
        if (fData && (fData.id || fData.headline || fData.bio)) {
          setFreelancerData({
            headline: fData.headline || "",
            bio: fData.bio || "",
            website: fData.website || "",
            languagesString: listToDisplayValue(fData.languages),
            skillsString: listToDisplayValue(fData.skills),
          });
          setHasFreelancerProfile(true);
        }
      } catch (err) {
        if (!cancelled) {
          if (err?.status !== 404) {
            logger.warn("Failed to load freelancer profile", err);
            setFreelancerError(
              isAr ? "تعذر تحميل بيانات ملف المستقل. يمكنك المحاولة لاحقًا." : "Unable to load the freelancer profile. Please try again later."
            );
          }
          setHasFreelancerProfile(false);
        }
      } finally {
        if (!cancelled) {
          setLoadingFreelancer(false);
        }
      }
    }
    loadFreelancerProfile();
    return () => {
      cancelled = true;
    };
  }, [isAr]);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview("");
      return;
    }

    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  useEffect(() => {
    let cancelled = false;

    async function loadDefaultAddress() {
      try {
        const addresses = await fetchMyAddresses();
        if (cancelled) return;
        const nextDefaultAddress =
          (Array.isArray(addresses) ? addresses.find((address) => Boolean(address.is_default)) : null) ||
          (Array.isArray(addresses) ? addresses[0] : null) ||
          null;
        setDefaultAddress(nextDefaultAddress);
      } catch (error) {
        if (!cancelled) {
          logger.warn("Failed to load default address for profile tab", error);
          setDefaultAddress(null);
        }
      }
    }

    loadDefaultAddress();

    return () => {
      cancelled = true;
    };
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function handleFreelancerChange(e) {
    const { name, value } = e.target;
    setFreelancerData((fd) => ({ ...fd, [name]: value }));
    setFreelancerError("");
  }

  function handleAvatarChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setAvatarFile(null);
      setAvatarError("");
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      setAvatarError("Only JPG, PNG, WebP, and GIF images are allowed");
      setAvatarFile(null);
      return;
    }

    if (file.size > maxSize) {
      setAvatarError("Image must be smaller than 5MB");
      setAvatarFile(null);
      return;
    }

    setAvatarError("");
    setAvatarImgError(false);
    setAvatarFile(file);
    // Convert to data URL for persistent display (survives save + blob revocation)
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarDataUrl(reader.result || "");
      setAvatarImgError(false);
    };
    reader.readAsDataURL(file);
  }

  async function savePersonalInfo(e) {
    e.preventDefault();
    setSavingPersonalInfo(true);
    setAvatarError("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", form.name || "");
      formDataToSend.append("phone", form.phone || "");

      if (avatarFile) {
        formDataToSend.append("avatar", avatarFile);
      } else if (form.avatar_url) {
        formDataToSend.append("avatar_url", form.avatar_url);
      }

      let res;
      try {
        // Send as FormData directly to profile/update
        res = await api.apiPost("profile/update", formDataToSend);
      } catch (err1) {
        // Fallback to user/profile
        res = await api.apiPut("user/profile", {
          name: form.name || "",
          phone: form.phone || "",
          avatar_url: form.avatar_url || user?.avatar_url || "",
        });
      }

      const updatedUser = res?.user || res?.data?.user || res?.data || {};
      // Use the raw server-returned avatar_url (relative path like /storage/avatars/...)  
      // Do NOT store the frontend-computed absolute URL back into the DB or form state
      const rawAvatarUrl = updatedUser?.avatar_url || res?.avatar_url || user?.avatar_url || "";
      const absoluteAvatarUrl = toAbsoluteUrl(rawAvatarUrl);
      const displayAvatarUrl = absoluteAvatarUrl ? `${absoluteAvatarUrl.split('?')[0]}?t=${Date.now()}` : "";

      const finalUser = {
        ...user,
        ...updatedUser,
        name: updatedUser?.name || form.name || user?.name,
        phone: updatedUser?.phone || form.phone || user?.phone,
        // Keep the original relative path in the user object so it resolves correctly on refresh
        avatar_url: rawAvatarUrl,
      };

      setForm((f) => ({
        ...f,
        name: finalUser.name,
        phone: finalUser.phone,
        // Keep relative path in form state too
        avatar_url: rawAvatarUrl,
      }));

      // Pass the absolute URL for immediate display in parent components
      const displayUser = { ...finalUser, avatar_url: avatarDataUrl || displayAvatarUrl || rawAvatarUrl };
      if (onProfileUpdated) onProfileUpdated(displayUser);
      if (setUser) setUser(displayUser);

      setAvatarFile(null);
      setAvatarPreview("");
      // avatarDataUrl is intentionally kept so the image stays visible after save
      alert(isAr ? "تم حفظ البيانات الشخصية والصورة بنجاح" : "Personal information saved successfully");
    } catch (err) {
      logger.error("save personal info error", err);
      const errMsg = getValidationMessage(err, isAr);
      alert(errMsg);
    } finally {
      setSavingPersonalInfo(false);
    }
  }

  async function saveFreelancerProfile(e) {
    e.preventDefault();
    const headline = freelancerData.headline.trim();
    const bio = freelancerData.bio.trim();

    if (!headline || !bio) {
      setFreelancerError(
        isAr ? "يرجى إدخال العنوان المهني والنبذة التعريفية قبل الحفظ." : "Please enter a professional headline and bio before saving."
      );
      return;
    }

    setFreelancerError("");
    setSavingFreelancerData(true);

    try {
      // The freelancer-profile API accepts simple string arrays for these fields.
      const languages = listToItems(freelancerData.languagesString);
      const skills = listToItems(freelancerData.skillsString);

      const payload = {
        headline,
        bio,
        website: freelancerData.website.trim() || null,
        languages,
        skills,
      };

      // Flexible save logic: try primary method (PUT or POST) on v1, then fallback if needed
      let response;
      const primaryMethod = hasFreelancerProfile ? "PUT" : "POST";
      
      try {
        if (primaryMethod === "PUT") {
          response = await api.apiPut("v1/freelancer/profile", payload);
        } else {
          response = await api.apiPost("v1/freelancer/profile", payload);
        }
      } catch (errPrimary) {
        logger.warn(`Primary ${primaryMethod} v1/freelancer/profile failed, attempting fallback`, errPrimary);
        try {
          // Retry with alternative method (POST if PUT failed, or PUT if POST failed)
          if (primaryMethod === "POST") {
            response = await api.apiPut("v1/freelancer/profile", payload);
          } else {
            response = await api.apiPost("v1/freelancer/profile", payload);
          }
        } catch (errSecondary) {
          // Fallback to legacy endpoint without v1 prefix
          try {
            if (hasFreelancerProfile) {
              response = await api.apiPut("freelancer/profile", payload);
            } else {
              response = await api.apiPost("freelancer/profile", payload);
            }
          } catch (errLegacy) {
            try {
              response = await api.apiPost("profile/update", {
                name: form.name || "",
                phone: form.phone || "",
              });
            } catch (errUserProf) {
              throw errPrimary;
            }
          }
        }
      }

      // Keep the editor in sync with the API response.  The endpoint may
      // create the freelancer profile on its first save.
      const savedProfile = response?.data || response;
      if (savedProfile && typeof savedProfile === "object") {
        setFreelancerData((current) => ({
          ...current,
          headline: savedProfile.headline ?? current.headline,
          bio: savedProfile.bio ?? current.bio,
          website: savedProfile.website ?? current.website,
          languagesString: Array.isArray(savedProfile.languages)
            ? listToDisplayValue(savedProfile.languages)
            : current.languagesString,
          skillsString: Array.isArray(savedProfile.skills)
            ? listToDisplayValue(savedProfile.skills)
            : current.skillsString,
        }));
      }
      setHasFreelancerProfile(true);

      alert(isAr ? "تم حفظ بيانات العمل الحر بنجاح" : "Freelancer profile saved successfully");
    } catch (err) {
      logger.error("save freelancer profile error", err);
      setFreelancerError(getValidationMessage(err, isAr));
    } finally {
      setSavingFreelancerData(false);
    }
  }

  async function saveSecurity(e) {
    e.preventDefault();
    if (!form.password) {
      alert(isAr ? "الرجاء إدخال كلمة المرور الجديدة أولاً" : "Please enter a new password first");
      return;
    }
    setSavingSecurity(true);

    try {
      const payload = {
        name: form.name || "",
        phone: form.phone || "",
        password: form.password,
      };

      const res = await api.apiPost("profile/update", payload);

      if (res.user) {
        setForm((f) => ({
          ...f,
          password: "",
        }));
        alert(isAr ? "تم تغيير كلمة المرور بنجاح" : "Password updated successfully");
      } else {
        alert(res.message || (isAr ? "فشل تحديث كلمة المرور" : "Failed to update password"));
      }
    } catch (err) {
      logger.error("save security error", err);
      alert(isAr ? "خطأ أثناء تغيير كلمة المرور" : "Error updating password");
    } finally {
      setSavingSecurity(false);
    }
  }

  const avatarSrc = avatarDataUrl || toAbsoluteUrl(
    avatarPreview || form.avatar_url || (user && user.avatar_url) || ""
  );

  // Reset image error when avatar source changes (new upload or new URL)
  useEffect(() => {
    setAvatarImgError(false);
  }, [avatarSrc]);

  // Show image only when we have a source AND it hasn't failed to load
  const showAvatarImage = Boolean(avatarSrc) && !avatarImgError;

  const inputClassName = "form-control rounded-3";
  const sectionTitleClassName = "fw-semibold mb-3";
  const profileAddressTitle = [form.street_address, form.neighborhood, form.city]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
  const profileAddressDetails = [form.address, form.country_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" • ");
  const hasProfileAddress = Boolean(profileAddressTitle || profileAddressDetails);
  const primaryAddressTitle = hasProfileAddress
    ? profileAddressTitle || form.address || form.country_name
    : [defaultAddress?.street, defaultAddress?.neighborhood, defaultAddress?.city]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(", ");
  const primaryAddressDetails = hasProfileAddress
    ? profileAddressDetails
    : String(defaultAddress?.address_text || "").trim();
  const primaryAddressBadge = hasProfileAddress
    ? isAr
      ? "عنوان الحساب"
      : "Account address"
    : isAr
      ? "العنوان الافتراضي"
      : "Default address";

  return (
    <div className="profile-tab-content">
      <div className="row g-4">
        {/* Personal Info & Avatar Form */}
        <form onSubmit={savePersonalInfo} className="col-12 mb-2">
          <div className="row g-4">
            {/* Avatar Uploader Card */}
            <div className="col-12 col-xl-4">
              <div className="border rounded-4 p-4 h-100">
                <div className="d-flex align-items-center gap-2 mb-3 border-bottom pb-2">
                  <Camera size={18} style={{ color: "var(--acc-orange)" }} />
                  <span className="fw-bold" style={{ color: "var(--acc-text)", fontSize: "15px" }}>
                    {labels.avatar || (isAr ? "الصورة الشخصية" : "Avatar")}
                  </span>
                </div>

                <div className="d-flex flex-column align-items-center text-center">
                  <div className="mb-3 position-relative">
                    {/* Show EITHER the image OR the placeholder - never both */}
                    {showAvatarImage ? (
                      <img
                        key={avatarSrc}
                        src={avatarSrc}
                        alt="avatar"
                        style={{
                          width: 104,
                          height: 104,
                          objectFit: "cover",
                          borderRadius: "50%",
                          border: "3px solid var(--acc-orange)",
                          boxShadow: "0 4px 15px rgba(255, 181, 34, 0.25)",
                        }}
                        onError={(e) => {
                          const currentSrc = e.currentTarget.src || "";
                          if (currentSrc.includes("/laravel-api/storage/") && !e.currentTarget.dataset.retried) {
                            e.currentTarget.dataset.retried = "true";
                            e.currentTarget.src = currentSrc.replace("/laravel-api/storage/", "/laravel-api/public/storage/");
                            return;
                          }
                          setAvatarImgError(true);
                        }}
                      />
                    ) : (
                      <div
                        className="avatar-placeholder d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: 104,
                          height: 104,
                          fontSize: 36,
                          fontWeight: 800,
                          background: "var(--acc-navy-700)",
                          color: "var(--acc-orange)",
                          border: "3px solid var(--acc-orange)",
                        }}
                      >
                        {String(form.name || user?.name || "U").trim().charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="w-100">
                    <input
                      type="file"
                      accept="image/*"
                      className={`${inputClassName} mb-2`}
                      onChange={handleAvatarChange}
                    />

                    {avatarError && <div className="alert alert-danger py-2 mb-2">{avatarError}</div>}

                    <small className="text-muted d-block mb-2" style={{ fontSize: "12px" }}>
                      {labels.avatar_hint ||
                        (isAr
                          ? "يمكنك رفع صورة من جهازك أو وضع رابط صورة مباشر"
                          : "You can upload an image or paste an avatar URL")}
                    </small>

                    <input
                      type="text"
                      name="avatar_url"
                      className={inputClassName}
                      value={form.avatar_url}
                      onChange={handleChange}
                      placeholder="https://example.com/avatar.png"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Info Form Card */}
            <div className="col-12 col-xl-8">
              <div className="border rounded-4 p-4 h-100">
                <div className="d-flex align-items-center gap-2 mb-3 border-bottom pb-2">
                  <User size={18} style={{ color: "var(--acc-orange)" }} />
                  <span className="fw-bold" style={{ color: "var(--acc-text)", fontSize: "15px" }}>
                    {labels.personal || (isAr ? "البيانات الشخصية" : "Personal Information")}
                  </span>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="profile-name" className="form-label">{labels.name || (isAr ? "الاسم" : "Name")}</label>
                    <input
                      id="profile-name"
                      type="text"
                      name="name"
                      className={inputClassName}
                      value={form.name}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="profile-email" className="form-label">{labels.email || (isAr ? "البريد الإلكتروني" : "Email")}</label>
                    <input
                      id="profile-email"
                      type="email"
                      className={inputClassName}
                      value={user?.email || ""}
                      disabled
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="profile-phone" className="form-label">
                      {labels.phone || (isAr ? "الهاتف" : "Phone")}
                      {!form.phone && <span className="text-danger"> *</span>}
                    </label>
                    <input
                      id="profile-phone"
                      type="text"
                      name="phone"
                      className={inputClassName}
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={isAr ? "أضف رقم هاتفك" : "Add your phone number"}
                      style={!form.phone ? { borderColor: "#dc3545", backgroundColor: "rgba(220, 53, 69, 0.05)" } : {}}
                      aria-describedby={!form.phone ? "profile-phone-hint" : undefined}
                    />
                    {!form.phone && (
                      <small id="profile-phone-hint" className="text-danger d-block mt-1">
                        {isAr ? "الرجاء إضافة رقم هاتفك" : "Please add your phone number"}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="profile-country" className="form-label">{labels.country || (isAr ? "الدولة" : "Country")}</label>
                    <input
                      id="profile-country"
                      type="text"
                      className={inputClassName}
                      value={user?.country_name || (isAr ? "غير محدد" : "Not set")}
                      disabled
                    />
                  </div>

                  <div className="col-12 mt-4 text-end">
                    <button
                      type="submit"
                      className="btn btn-primary rounded-pill px-4 py-2 fw-bold d-inline-flex align-items-center gap-2"
                      disabled={savingPersonalInfo}
                    >
                      <Save size={16} />
                      <span>
                        {savingPersonalInfo
                          ? (isAr ? "جارِ الحفظ..." : "Saving...")
                          : (isAr ? "حفظ البيانات الشخصية" : "Save Personal Info")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Freelancer Profile Section */}
        {loadingFreelancer ? (
          <div className="col-12 text-center py-4 my-2 border rounded-4">
            <div className="spinner-border spinner-border-sm text-warning" role="status" />
            <span className="ms-2 small text-muted">{isAr ? "تحميل بيانات المستقل..." : "Loading freelancer data..."}</span>
          </div>
        ) : hasFreelancerProfile ? (
          <form onSubmit={saveFreelancerProfile} className="col-12 mb-2">
            <div className="border rounded-4 p-4">
              <div className="d-flex align-items-center gap-3 border-bottom pb-3 mb-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 40, height: 40, background: "rgba(255, 181, 34, 0.15)", color: "var(--acc-orange)" }}
                >
                  <Briefcase size={20} />
                </div>
                <div>
                  <h5 className="fw-bold mb-0" style={{ fontSize: "1.05rem", color: "var(--acc-text)" }}>
                    {isAr ? "بيانات العمل الحر (ملف المستقل)" : "Freelancer Profile Details"}
                  </h5>
                  <small className="text-muted fw-normal" style={{ fontSize: "12.5px" }}>
                    {isAr ? "تعديل النبذة والعنوان التعريفي والمهارات ليظهر للعملاء" : "Manage your public bio, profession headline, and skills"}
                  </small>
                </div>
              </div>

              {freelancerError ? (
                <div className="alert alert-danger mb-4" role="alert" style={{ whiteSpace: "pre-line" }}>
                  {freelancerError}
                </div>
              ) : null}

              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="freelancer-headline" className="form-label">{isAr ? "العنوان المهني (Headline)" : "Professional Headline"}</label>
                  <input
                    id="freelancer-headline"
                    type="text"
                    name="headline"
                    className={inputClassName}
                    value={freelancerData.headline}
                    onChange={handleFreelancerChange}
                    required
                    placeholder={isAr ? "مثال: مبرمج لارافيل محترف" : "e.g., Laravel Backend Developer"}
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="freelancer-website" className="form-label">{isAr ? "الموقع الإلكتروني" : "Website URL"}</label>
                  <input
                    id="freelancer-website"
                    type="url"
                    name="website"
                    className={inputClassName}
                    value={freelancerData.website}
                    onChange={handleFreelancerChange}
                    placeholder="https://mywebsite.com"
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="freelancer-languages" className="form-label">{isAr ? "اللغات (مفصولة بفاصلة)" : "Languages (comma-separated)"}</label>
                  <input
                    id="freelancer-languages"
                    type="text"
                    name="languagesString"
                    className={inputClassName}
                    value={freelancerData.languagesString}
                    onChange={handleFreelancerChange}
                    placeholder={isAr ? "العربية, English" : "العربية, English"}
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="freelancer-skills" className="form-label">{isAr ? "المهارات (مفصولة بفاصلة)" : "Skills (comma-separated)"}</label>
                  <input
                    id="freelancer-skills"
                    type="text"
                    name="skillsString"
                    className={inputClassName}
                    value={freelancerData.skillsString}
                    onChange={handleFreelancerChange}
                    placeholder={isAr ? "Laravel, React, API" : "Laravel, React, API"}
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="freelancer-bio" className="form-label">{isAr ? "النبذة التعريفية (Bio)" : "Biography / Bio"}</label>
                  <textarea
                    id="freelancer-bio"
                    name="bio"
                    rows="4"
                    className={inputClassName}
                    value={freelancerData.bio}
                    onChange={handleFreelancerChange}
                    required
                    placeholder={isAr ? "اكتب نبذة عن خبراتك ومهاراتك وما يمكنك تقديمه للعملاء..." : "Write a short summary about your skills, experience, and what you offer..."}
                  />
                </div>

                <div className="col-12 mt-4 text-end">
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4 py-2 fw-bold d-inline-flex align-items-center gap-2"
                    disabled={savingFreelancerData}
                  >
                    <Save size={16} />
                    <span>
                      {savingFreelancerData
                        ? (isAr ? "جارِ الحفظ..." : "Saving...")
                        : (isAr ? "حفظ بيانات العمل الحر" : "Save Freelancer Details")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="col-12 mb-2">
            <div className="border rounded-4 p-4 text-center">
              <div className="mb-3" style={{ color: "var(--acc-orange)" }}>
                <Briefcase size={36} />
              </div>
              <h5 className="fw-bold mb-2" style={{ color: "var(--acc-text)" }}>
                {isAr ? "هل تود العمل كمستقل؟" : "Want to work as a Freelancer?"}
              </h5>
              <p className="text-muted small mb-3 max-w-md mx-auto">
                {isAr
                  ? "بتفعيل حساب المستقل، ستتمكن من تقديم خدماتك وإظهار مهاراتك ونبذتك المهنية لعملائك على المنصة."
                  : "By activating, you can start offering services and sharing your professional skills and bio with clients."}
              </p>
              <button
                type="button"
                className="btn btn-dark rounded-pill px-4 py-2 fw-bold d-inline-flex align-items-center gap-2"
                onClick={() => setHasFreelancerProfile(true)}
              >
                <Sparkles size={16} className="text-warning" />
                <span>{isAr ? "تفعيل ملف المستقل" : "Activate Freelancer Profile"}</span>
              </button>
            </div>
          </div>
        )}

        {/* Primary Address Section */}
        <div className="col-12 mb-2">
          <div className="border rounded-4 p-4">
            <div className="d-flex align-items-center gap-2 mb-3 border-bottom pb-2">
              <MapPin size={18} style={{ color: "var(--acc-orange)" }} />
              <span className="fw-bold" style={{ color: "var(--acc-text)", fontSize: "15px" }}>
                {isAr ? "العنوان الأساسي" : "Primary Address"}
              </span>
            </div>

            {hasProfileAddress || defaultAddress ? (
              <div className="border rounded-4 p-3 p-md-4">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                  <div>
                    <div className="fw-bold mb-1" style={{ color: "var(--acc-text)" }}>
                      {primaryAddressTitle || (isAr ? "عنوان المستخدم" : "User address")}
                    </div>
                    {primaryAddressDetails ? (
                      <div className="text-muted small">{primaryAddressDetails}</div>
                    ) : null}
                  </div>
                  <span className="badge bg-success px-3 py-2 rounded-pill">
                    {primaryAddressBadge}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-muted small">
                {isAr
                  ? "لا يوجد عنوان محفوظ بعد. أضف عنوانك من تبويب العناوين."
                  : "No saved address yet. Add one from the Addresses tab."}
              </div>
            )}

            <div className="mt-3 text-end">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4 fw-bold"
                onClick={() => onOpenAddresses?.()}
              >
                {isAr ? "إدارة العناوين" : "Manage addresses"}
              </button>
            </div>
          </div>
        </div>

        {/* Security / Password Form */}
        <form onSubmit={saveSecurity} className="col-12">
          <div className="border rounded-4 p-4">
            <div className="d-flex align-items-center gap-2 mb-3 border-bottom pb-2">
              <Lock size={18} style={{ color: "var(--acc-orange)" }} />
              <span className="fw-bold" style={{ color: "var(--acc-text)", fontSize: "15px" }}>
                {isAr ? "الأمان وتغيير كلمة المرور" : "Security & Password"}
              </span>
            </div>

            <div className="row g-3 align-items-end">
              <div className="col-md-8">
                <label className="form-label">
                  {labels.password || (isAr ? "كلمة مرور جديدة" : "New Password")}
                </label>
                <input
                  type="password"
                  name="password"
                  className={inputClassName}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                />
              </div>

              <div className="col-md-4">
                <button
                  type="submit"
                  className="btn btn-primary w-100 rounded-pill py-2 fw-bold d-inline-flex align-items-center justify-content-center gap-2"
                  disabled={savingSecurity}
                >
                  <Lock size={16} />
                  <span>
                    {savingSecurity
                      ? (isAr ? "جارِ الحفظ..." : "Saving...")
                      : (isAr ? "تغيير كلمة المرور" : "Change Password")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}



